import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import useCartStore from '../store/cartStore';

export const Cart = () => {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, getTotal } = useCartStore();

  const handleCheckout = () => {
    if (items.length === 0) return;
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-ivory pt-24 flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center px-6">
          <ShoppingBag className="w-16 h-16 text-gold mx-auto mb-6" strokeWidth={1} />
          <h2 className="font-heading text-3xl md:text-4xl font-light text-charcoal mb-4">
            Your Cart is Empty
          </h2>
          <p className="text-charcoal-light mb-8 max-w-md mx-auto">
            Looks like you haven't added anything to your cart yet. Explore our collections to find something you'll love.
          </p>
          <Link 
            to="/products"
            className="inline-flex items-center gap-3 bg-charcoal text-ivory px-10 py-4 text-xs tracking-[0.15em] uppercase font-medium hover:bg-charcoal/90 transition-colors"
            data-testid="continue-shopping-button"
          >
            Explore Collections
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory pt-24" data-testid="cart-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-12">
          <span className="overline mb-3 block">Shopping</span>
          <h1 className="font-heading text-4xl md:text-5xl font-light text-charcoal" data-testid="cart-title">
            Your Cart
          </h1>
          <p className="text-charcoal-light mt-2">{items.length} {items.length === 1 ? 'item' : 'items'}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
            {items.map((item, index) => {
              const price = item.product.discount_price || item.product.price;
              return (
                <div 
                  key={`${item.product.id}-${item.size}-${item.color}-${item.variantId}`} 
                  className="flex gap-6 pb-6 border-b border-charcoal/10"
                  data-testid={`cart-item-${index}`}
                >
                  <img 
                    src={item.product.images?.[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80'} 
                    alt={item.product.name}
                    className="w-28 h-36 object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80';
                    }}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-charcoal mb-2">{item.product.name}</h3>
                        <div className="flex flex-wrap gap-4 text-sm text-charcoal-light mb-3">
                          <span>Size: <span className="text-charcoal">{item.size}</span></span>
                          {item.color && item.color !== 'Standard' && (
                            <span>Color: <span className="text-charcoal">{item.color}</span></span>
                          )}
                        </div>
                        <p className="text-lg font-medium text-charcoal">₹{price.toLocaleString()}</p>
                      </div>
                      <button
                        onClick={() => removeItem(item.product.id, item.size, item.color, item.variantId)}
                        className="p-2 text-charcoal-light hover:text-charcoal transition-colors"
                        data-testid={`remove-item-${index}`}
                      >
                        <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                      </button>
                    </div>
                    
                    {/* Quantity */}
                    <div className="flex items-center gap-4 mt-4">
                      <span className="text-xs tracking-[0.1em] uppercase text-charcoal-light">Qty:</span>
                      <div className="flex items-center border border-charcoal/20">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.size, item.color, item.variantId, Math.max(1, item.quantity - 1))}
                          className="px-3 py-2 hover:bg-beige transition-colors"
                          data-testid={`decrease-quantity-${index}`}
                        >
                          <Minus className="w-4 h-4 text-charcoal" strokeWidth={1.5} />
                        </button>
                        <span className="px-4 py-2 font-medium text-charcoal border-x border-charcoal/20" data-testid={`quantity-${index}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.size, item.color, item.variantId, item.quantity + 1)}
                          className="px-3 py-2 hover:bg-beige transition-colors"
                          data-testid={`increase-quantity-${index}`}
                        >
                          <Plus className="w-4 h-4 text-charcoal" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-beige p-8 sticky top-28" data-testid="cart-summary">
              <h2 className="font-heading text-2xl font-light text-charcoal mb-8">Order Summary</h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex justify-between text-sm">
                  <span className="text-charcoal-light">Subtotal</span>
                  <span className="text-charcoal" data-testid="subtotal">₹{getTotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-charcoal-light">Shipping</span>
                  <span className="text-gold">Free</span>
                </div>
                <div className="border-t border-charcoal/10 pt-4 flex justify-between">
                  <span className="font-medium text-charcoal">Total</span>
                  <span className="font-heading text-2xl text-charcoal" data-testid="total">
                    ₹{getTotal().toLocaleString()}
                  </span>
                </div>
              </div>
              
              <button
                onClick={handleCheckout}
                className="w-full bg-charcoal text-ivory py-4 text-xs tracking-[0.15em] uppercase font-medium hover:bg-charcoal/90 transition-colors mb-4"
                data-testid="proceed-to-checkout-button"
              >
                Proceed to Checkout
              </button>
              
              <Link 
                to="/products"
                className="block w-full text-center py-4 border border-charcoal text-charcoal text-xs tracking-[0.15em] uppercase font-medium hover:bg-charcoal hover:text-ivory transition-colors"
                data-testid="continue-shopping-link"
              >
                Continue Shopping
              </Link>
              
              <p className="text-xs text-charcoal-light text-center mt-6">
                Taxes and discounts calculated at checkout
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
